import { FC, useEffect, useRef, useState } from 'react';
import { Button, Span, LoaderIndicator, Tabs } from 'tedooo-web-design-system/ui';
import classNames from 'classnames';
import {
  Camera,
  Edit,
  Message,
  MoreHorizontal,
  PlusIcon,
  Star,
} from 'tedooo-web-design-system/icons';
import { useNavigate } from 'react-router-dom';
import Lottie from 'react-lottie';

import shopIcon from 'photos/coverPhoto.png';
import { IShopHeader } from 'types';
import { Image } from 'components/helpers';
import Rate from 'components/rate/Rate';
import { translations } from 'translations/en';
import './header.scss';
import { ShopPageTabs } from '../helper';
import FollowButton from 'components/followButton/FollowButton';
import ShippingToTag from 'components/shippingToTag/ShippingToTag';
import { uploadImage } from 'api';
import { ROUTES } from 'constant';
import Animation from 'lotties/add-avatar-lottie.json';
import { useRequireToken } from 'hooks/useRequireToken';
import { checkSmallScreen, toShortFormat } from 'utils';
import { Avatar } from 'components/avatar/Avatar';
import { getDefaultAvatar } from '../../../store/user/helper';
import UserDetailsBadges from 'components/post/header/userDetails/badges/UserDetailsBadges';

const { FOLLOWERS, FOLLOWER, FOLLOWING, MESSAGE } = translations.COMMON;

const lottieOptions = {
  loop: true,
  autoplay: true,
  animationData: Animation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const isSmallScreen = checkSmallScreen();

const Header: FC<IShopHeader> = ({
  userName,
  image,
  rate,
  shopName,
  isPremium,
  location,
  isReviewEnabled,
  totalFollowers,
  totalFollowing,
  totalReviews,
  isFollowing,
  onFollowToggle,
  originCountry,
  shippingCountries,
  onMessage,
  onTabClick,
  selectedTab,
  isShopOwner,
  onReviewClick,
  onAddItemToCatalogClick,
  onEditProfileClick,
  onMoreClick,
  onFollowersClick,
  editCoverPhoto,
  editAvatar,
  avatar,
  userId,
  isVisionSeller,
}) => {
  const src = image || shopIcon;
  useEffect(() => {
    setCoverPhoto(src);
  }, [image]);

  const shouldDisplayLottie = !avatar;

  const navigate = useNavigate();

  const followersText = totalFollowers === 1 ? FOLLOWER : FOLLOWERS;

  const [isLoading, setIsLoading] = useState(false);

  const [coverPhoto, setCoverPhoto] = useState(src);

  const ref = useRef<HTMLInputElement>(null);

  const avatarRef = useRef<HTMLInputElement>(null);

  const onEditCoverClick = () => {
    ref.current!.click();
  };

  const onEditAvatarClick = () => {
    avatarRef.current!.click();
  };

  const onFollowingClick = () => {
    if (isShopOwner) {
      navigate(`/${ROUTES.FOLLOWING}`);
    }
  };

  const onSuccess = () => {
    setIsLoading(false);
  };

  const onChangeCoverPhoto = async (e: any) => {
    const file = e.target.files[0] || null;
    setIsLoading(true);

    const imageUrl = await uploadImage({
      file,
      onSuccess,
    });
    setCoverPhoto(imageUrl);
    editCoverPhoto({ coverPhoto: imageUrl });
  };

  const onChangeAvatar = async (e: any) => {
    const file = e.target.files[0] || null;

    const imageUrl = await uploadImage({
      file,
    });
    editAvatar({ avatar: imageUrl });
  };

  const onClick = useRequireToken(() => onReviewClick());

  const renderButtons = () => {
    if (isShopOwner) {
      return (
        <>
          <Button
            type="primary"
            size="medium"
            borderRadius="small"
            text="Add item to Catalog"
            prefixIcon={<PlusIcon />}
            onClick={onAddItemToCatalogClick}
          />
          <Button
            type="secondary"
            size="medium"
            borderRadius="small"
            text="Edit Shop"
            prefixIcon={<Edit />}
            onClick={onEditProfileClick}
          />
        </>
      );
    }

    return (
      <>
        <FollowButton isFollowing={isFollowing} onFollowToggle={onFollowToggle} />
        {isReviewEnabled && (
          <Button
            type="secondary"
            size="medium"
            borderRadius="small"
            text="Write a review"
            prefixIcon={<Star />}
            onClick={onClick}
            className="review-button"
          />
        )}
        <Button
          borderRadius="small"
          size="medium"
          text={MESSAGE}
          type={isFollowing ? 'primary' : 'secondary'}
          prefixIcon={<Message />}
          onClick={onMessage}
        />
        <Button
          type="secondary"
          size="medium"
          borderRadius="small"
          prefixIcon={<MoreHorizontal />}
          className="more-button"
          onClick={onMoreClick}
        />
      </>
    );
  };

  const formattedFollowers = toShortFormat(totalFollowers, 2);

  const formattedFollowing = toShortFormat(totalFollowing, 2);

  return (
    <div className="shop-header">
      <div className="cover-photo">
        <LoaderIndicator
          size={50}
          color="grey"
          className={classNames('spinner', { active: isLoading })}
        />
        <Image withOverlay={isLoading} className={classNames({ small: !image })} src={coverPhoto} />
        {isShopOwner && (
          <>
            <input
              ref={ref}
              type="file"
              name="file"
              id="cover-photo"
              className="inputfile"
              accept="image/x-png,image/jpeg,image/jpg"
              onChange={onChangeCoverPhoto}
            />
            <Button
              type="secondary"
              size="medium"
              borderRadius="small"
              prefixIcon={<Camera />}
              text={translations.SHOP.EDIT_COVER_PHOTO}
              className="edit-button"
              onClick={onEditCoverClick}
            />
          </>
        )}
      </div>
      <div className="profile-pic-container">
        {/* {stories && <Stories stories={stories} isProfile />} */}
        <Avatar
          className="avatar-shop"
          src={avatar || getDefaultAvatar(userId)}
          size={isSmallScreen ? 84 : 106}
        />

        {isShopOwner && (
          <div className="icon-container" onClick={onEditAvatarClick}>
            {!shouldDisplayLottie && <Camera />}
            {shouldDisplayLottie && (
              <Lottie options={lottieOptions} height="180%" isClickToPauseDisabled />
            )}
            <input
              ref={avatarRef}
              type="file"
              name="file"
              id="cover-photo"
              className="inputfile"
              accept="image/x-png,image/jpeg,image/jpg"
              onChange={onChangeAvatar}
            />
          </div>
        )}

        <div className="User-details-badges-container">
          <UserDetailsBadges isVisionSeller={isVisionSeller} isPremium={isPremium} size="large" />
        </div>
      </div>
      <div className="details">
        <div className="column">
          <div className="row">
            <Span text={shopName} className="title" />
          </div>
          <div className="text">
            <Span text={`@${userName}`} />
            <div className="dot" />
            <Span text={location} />
          </div>
          <div className="followers-following-container">
            <span
              className={classNames('follow-container', { clickable: isShopOwner })}
              onClick={onFollowersClick}
            >
              <span className="number"> {formattedFollowers}</span>
              {followersText}
            </span>
            <span
              className={classNames('follow-container', { clickable: isShopOwner })}
              onClick={onFollowingClick}
            >
              <span className="number"> {formattedFollowing}</span>
              {FOLLOWING}
            </span>
          </div>
          <Rate value={rate} totalReviews={totalReviews} shouldDisplayReviews={false} />
          <ShippingToTag originCountry={originCountry} shippingCountries={shippingCountries} />
          <div className="buttons">{renderButtons()}</div>
        </div>
        <div className="User-details-badges-container" />
      </div>
      <Tabs selectedTab={selectedTab} tabs={ShopPageTabs} onChange={onTabClick} />
    </div>
  );
};

export default Header;
